/**
 * Which rail and palette entries this person is not offered — ADR 9037, over ADR 9022's
 * capabilities read.
 *
 * A route may declare the capability it is for (`FeatureRoute.capability`). An entry whose route
 * the platform's answer DENIES is withheld: the person is not shown a link that leads to "you do
 * not have access", as User Management is not offered to someone its route would refuse
 * (ADR 9014). Unknown is not "no" (ADR 9022 decision 3): while the read loads, fails or is not
 * published nothing is withheld, and the route itself judges once it is answered.
 *
 * Pure over the answer and the route list, so the test asserts it without a network.
 */

import { useMemo } from 'react';

import type { MeCapabilities } from '../../api/generated';
import { judgeCapability, useMyCapabilities } from '../../auth/capabilities';
import { routes, type ResolvedRoute } from '../../features/registry';

/** A predicate over a navigation entry's `to`: `true` when its route is denied to this person. */
export function withheldPaths(
  answer: MeCapabilities | undefined,
  routeList: readonly ResolvedRoute[] = routes,
): (to: string) => boolean {
  if (answer === undefined) return () => false;
  const denied = new Set(routeList
    .filter(({ route }) => route.capability !== undefined
      && judgeCapability(answer, route.capability.kind, route.capability.action).state === 'denied')
    .map(({ path }) => path));
  return (to) => denied.has(to);
}

/** The session's predicate, from the one capabilities read every control shares. */
export function useWithheldPaths(): (to: string) => boolean {
  const read = useMyCapabilities();
  const answer = read.state === 'answered' ? read.data : undefined;
  return useMemo(() => withheldPaths(answer), [answer]);
}
